import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import {
  Save,
  Play,
  Loader2,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Check,
  X,
  AlertCircle,
  HelpCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  unifiedFunctionsApi,
  type UnifiedFunctionDetail,
  type TestFunctionResponse,
} from '@/services/unifiedFunctionsApi';
import { ConfigSchemaEditor } from '@/components/schemas/ConfigSchemaEditor';

export function FunctionEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [func, setFunc] = useState<UnifiedFunctionDetail | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Built-in functions are read-only
  const isBuiltin = func?.is_builtin ?? false;

  // Form state
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [code, setCode] = useState('');
  const [isTool, setIsTool] = useState(true);
  const [isEval, setIsEval] = useState(false);
  const [toolSchema, setToolSchema] = useState('{}');
  const [configSchema, setConfigSchema] = useState('{}');  // JSON Schema for eval function config
  const [capabilities, setCapabilities] = useState<Record<string, boolean>>({
    fetch: false,
    data: false,
    storage: false,
    trajectory: false,
    mcp: false,
    evals: false,
    content: false,
    secrets: false,
    agents: false,
  });
  const [mcpServers, setMcpServers] = useState('');  // comma-separated server names
  const [timeoutMs, setTimeoutMs] = useState(5000);
  const [returnsGroundTruth, setReturnsGroundTruth] = useState(false);
  const [supportsCannedData, setSupportsCannedData] = useState(false);

  // Test runner state - load from localStorage
  const [testOpen, setTestOpen] = useState(false);
  const [testParams, setTestParams] = useState(() => {
    if (id) {
      return localStorage.getItem(`func-test-params-${id}`) || '{}';
    }
    return '{}';
  });
  const [testEvalContext, setTestEvalContext] = useState(() => {
    if (id) {
      return localStorage.getItem(`func-test-eval-context-${id}`) || '{}';
    }
    return '{}';
  });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestFunctionResponse | null>(null);

  // Persist test inputs to localStorage
  useEffect(() => {
    if (id && testParams !== '{}') {
      localStorage.setItem(`func-test-params-${id}`, testParams);
    }
  }, [id, testParams]);

  useEffect(() => {
    if (id && testEvalContext !== '{}') {
      localStorage.setItem(`func-test-eval-context-${id}`, testEvalContext);
    }
  }, [id, testEvalContext]);

  // Load function
  useEffect(() => {
    if (isNew) {
      setCode(`def execute(params: dict, context: dict) -> dict:
    """
    Execute the function.

    Args:
        params: Input parameters from the tool call
        context: Execution context (mode, eval_context, etc.)

    Returns:
        Result dictionary
    """
    # Your code here
    return {"result": "Hello, World!"}
`);
      return;
    }

    const fetchFunction = async () => {
      try {
        setLoading(true);
        const data = await unifiedFunctionsApi.get(id!);
        setFunc(data);
        setName(data.name);
        setDisplayName(data.display_name || '');
        setDescription(data.description || '');
        setCode(data.code);
        setIsTool(data.is_tool);
        setIsEval(data.is_eval_function);
        setToolSchema(JSON.stringify(data.tool_schema || {}, null, 2));
        setConfigSchema(JSON.stringify(data.config_schema || {}, null, 2));
        setTimeoutMs(data.timeout_ms);
        setCapabilities({
          fetch: !!data.capabilities?.fetch,
          data: !!data.capabilities?.data,
          storage: !!data.capabilities?.storage,
          trajectory: !!data.capabilities?.trajectory,
          mcp: !!data.capabilities?.mcp,
          evals: !!data.capabilities?.evals,
          content: !!data.capabilities?.content,
          secrets: !!data.capabilities?.secrets,
          agents: !!data.capabilities?.agents,
        });
        // Load MCP servers as comma-separated string
        const mcpConfig = data.capabilities?.mcp;
        if (mcpConfig && mcpConfig.servers) {
          setMcpServers(mcpConfig.servers.join(', '));
        }
        setReturnsGroundTruth(data.returns_ground_truth || false);
        setSupportsCannedData(data.supports_canned_data || false);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load function');
      } finally {
        setLoading(false);
      }
    };

    fetchFunction();
  }, [id, isNew]);

  // Track changes
  useEffect(() => {
    if (!func) return;
    // Compare capabilities
    const originalMcpServers = func.capabilities?.mcp?.servers?.join(', ') || '';
    const capabilitiesChanged =
      capabilities.fetch !== !!func.capabilities?.fetch ||
      capabilities.data !== !!func.capabilities?.data ||
      capabilities.storage !== !!func.capabilities?.storage ||
      capabilities.trajectory !== !!func.capabilities?.trajectory ||
      capabilities.mcp !== !!func.capabilities?.mcp ||
      capabilities.evals !== !!func.capabilities?.evals ||
      capabilities.content !== !!func.capabilities?.content ||
      capabilities.secrets !== !!func.capabilities?.secrets ||
      capabilities.agents !== !!func.capabilities?.agents ||
      mcpServers !== originalMcpServers;

    // Compare tool schema (parse current string to compare objects)
    let toolSchemaChanged = false;
    try {
      const currentSchema = JSON.parse(toolSchema);
      const originalSchema = func.tool_schema || {};
      toolSchemaChanged = JSON.stringify(currentSchema) !== JSON.stringify(originalSchema);
    } catch {
      // If current schema is invalid JSON, consider it changed (will show error on save)
      toolSchemaChanged = true;
    }

    // Compare config schema for eval functions
    let configSchemaChanged = false;
    try {
      const currentConfigSchema = JSON.parse(configSchema);
      const originalConfigSchema = func.config_schema || {};
      configSchemaChanged = JSON.stringify(currentConfigSchema) !== JSON.stringify(originalConfigSchema);
    } catch {
      configSchemaChanged = true;
    }

    const hasChanges =
      name !== func.name ||
      displayName !== (func.display_name || '') ||
      description !== (func.description || '') ||
      code !== func.code ||
      isTool !== func.is_tool ||
      isEval !== func.is_eval_function ||
      timeoutMs !== func.timeout_ms ||
      returnsGroundTruth !== (func.returns_ground_truth || false) ||
      supportsCannedData !== (func.supports_canned_data || false) ||
      capabilitiesChanged ||
      toolSchemaChanged ||
      configSchemaChanged;
    setHasChanges(hasChanges);
  }, [func, name, displayName, description, code, isTool, isEval, timeoutMs, returnsGroundTruth, supportsCannedData, capabilities, mcpServers, toolSchema, configSchema]);

  const handleSave = async () => {
    // Validate name is not empty
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Function name is required');
      return;
    }

    // Prevent UUID-formatted names (likely a mistake)
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (uuidPattern.test(trimmedName)) {
      setError('Function name cannot be a UUID');
      return;
    }

    if (!isTool && !isEval) {
      setError('Function must be either a tool or eval function (or both)');
      return;
    }

    let parsedSchema = {};
    try {
      parsedSchema = JSON.parse(toolSchema);
    } catch {
      setError('Invalid tool schema JSON');
      return;
    }

    let parsedConfigSchema = {};
    if (isEval) {
      try {
        parsedConfigSchema = JSON.parse(configSchema);
      } catch {
        setError('Invalid config schema JSON');
        return;
      }
    }

    const capabilitiesObj: Record<string, unknown> = {};
    if (capabilities.fetch) capabilitiesObj.fetch = { domains: [] };
    if (capabilities.data) capabilitiesObj.data = true;
    if (capabilities.storage) capabilitiesObj.storage = { namespace: 'default' };
    if (capabilities.trajectory) capabilitiesObj.trajectory = { read: true };
    if (capabilities.mcp && mcpServers.trim()) {
      const servers = mcpServers.split(',').map(s => s.trim()).filter(s => s);
      capabilitiesObj.mcp = { servers, max_calls: 10 };
    }
    if (capabilities.evals) capabilitiesObj.evals = { read: true, write: true };
    if (capabilities.content) capabilitiesObj.content = { read: true, write: true };
    if (capabilities.secrets) capabilitiesObj.secrets = { allowed: [] };  // User needs to specify allowed secrets
    if (capabilities.agents) capabilitiesObj.agents = { run: true };

    try {
      setSaving(true);
      setError(null);

      if (isNew) {
        const newFunc = await unifiedFunctionsApi.create({
          name: trimmedName,
          code,
          is_tool: isTool,
          is_eval_function: isEval,
          display_name: displayName || undefined,
          description: description || undefined,
          tool_schema: parsedSchema,
          config_schema: isEval ? parsedConfigSchema : undefined,
          timeout_ms: timeoutMs,
          capabilities: capabilitiesObj,
          returns_ground_truth: returnsGroundTruth,
          supports_canned_data: supportsCannedData,
        });
        navigate(`/developer/functions/${newFunc.id}`, { replace: true });
      } else {
        const updated = await unifiedFunctionsApi.update(id!, {
          name: trimmedName,
          code,
          is_tool: isTool,
          is_eval_function: isEval,
          display_name: displayName || undefined,
          description: description || undefined,
          tool_schema: parsedSchema,
          config_schema: isEval ? parsedConfigSchema : undefined,
          timeout_ms: timeoutMs,
          capabilities: capabilitiesObj,
          returns_ground_truth: returnsGroundTruth,
          supports_canned_data: supportsCannedData,
        });
        setFunc(updated);
        setHasChanges(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save function');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (isNew) {
      setError('Save the function before testing');
      return;
    }

    let parsedParams = {};
    try {
      parsedParams = JSON.parse(testParams);
    } catch {
      setError('Invalid test params JSON');
      return;
    }

    let parsedEvalContext = undefined;
    if (isEval) {
      try {
        parsedEvalContext = JSON.parse(testEvalContext);
      } catch {
        setError('Invalid eval context JSON');
        return;
      }
    }

    try {
      setTesting(true);
      setError(null);
      const result = await unifiedFunctionsApi.test(id!, {
        params: parsedParams,
        mode: isEval ? 'eval' : 'tool',
        eval_context: parsedEvalContext,
      });
      setTestResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  // Keyboard shortcut for save
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (hasChanges || isNew) {
          handleSave();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasChanges, isNew, handleSave]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-56px)] flex flex-col">
      {/* Header */}
      <div className="border-b px-4 py-3 flex items-center justify-between bg-background">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/developer/functions')}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Functions
          </Button>
          <Separator orientation="vertical" className="h-6" />
          <span className="font-medium">{isNew ? 'New Function' : name}</span>
          {isBuiltin && <Badge variant="secondary">Built-in (Read-only)</Badge>}
          {!isBuiltin && hasChanges && <Badge variant="secondary">Unsaved</Badge>}
        </div>
        <div className="flex items-center gap-2">
          {!isBuiltin && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTestOpen(!testOpen)}
                disabled={isNew}
              >
                <Play className="h-4 w-4 mr-2" />
                Test
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving || (!hasChanges && !isNew)}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save
              </Button>
            </>
          )}
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mx-4 mt-4 mb-0">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Code Editor (left) */}
        <div className="flex-1 flex flex-col border-r">
          <Editor
            height="100%"
            defaultLanguage="python"
            value={code}
            onChange={(value) => !isBuiltin && setCode(value || '')}
            theme="vs-dark"
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              lineNumbers: 'on',
              scrollBeyondLastLine: false,
              wordWrap: 'on',
              automaticLayout: true,
              readOnly: isBuiltin,
            }}
          />
        </div>

        {/* Config Panel (right) */}
        <div className="w-80 overflow-y-auto bg-muted/30 p-4 space-y-4">
          {isBuiltin ? (
            /* Read-only info for built-in functions */
            <>
              <div className="space-y-2">
                <Label>Name</Label>
                <p className="text-sm">{name}</p>
              </div>
              {displayName && (
                <div className="space-y-2">
                  <Label>Display Name</Label>
                  <p className="text-sm">{displayName}</p>
                </div>
              )}
              {description && (
                <div className="space-y-2">
                  <Label>Description</Label>
                  <p className="text-sm text-muted-foreground">{description}</p>
                </div>
              )}
              <Separator />
              <div className="space-y-2">
                <Label>Type</Label>
                <Badge variant="outline" className="text-green-600 border-green-600">
                  Eval Function
                </Badge>
              </div>
            </>
          ) : (
            /* Editable form for user functions */
            <>
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="my_function"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="displayName">Display Name</Label>
                <Input
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="My Function"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What does this function do?"
                  rows={2}
                />
              </div>

              <Separator />

              <div className="space-y-2">
                <Label>Function Type</Label>
                <div className="flex gap-4">
                  <Badge variant={isTool ? "default" : "outline"}>
                    {isTool ? "Tool" : "Not a Tool"}
                  </Badge>
                  <Badge variant={isEval ? "default" : "outline"}>
                    {isEval ? "Eval" : "Not Eval"}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  Type is set at creation and cannot be changed
                </p>
              </div>

              {/* Eval-specific options */}
              {isEval && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="returns_ground_truth"
                      checked={returnsGroundTruth}
                      onCheckedChange={(checked) => setReturnsGroundTruth(checked as boolean)}
                    />
                    <Label htmlFor="returns_ground_truth" className="cursor-pointer font-normal">
                      Returns Ground Truth
                    </Label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Enable if this function computes the correct/expected answer
                  </p>
                </div>
              )}

              {/* Tool-specific options */}
              {isTool && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="supports_canned_data"
                      checked={supportsCannedData}
                      onCheckedChange={(checked) => setSupportsCannedData(checked as boolean)}
                    />
                    <Label htmlFor="supports_canned_data" className="cursor-pointer font-normal">
                      Supports Canned Data
                    </Label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Enable to allow mock responses for testing
                  </p>
                </div>
              )}

              <Separator />

              <div className="space-y-2">
                <Label>Capabilities</Label>
                <p className="text-xs text-muted-foreground">
                  Select what resources this function can access
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="cap_fetch"
                      checked={capabilities.fetch}
                      onCheckedChange={(checked) =>
                        setCapabilities({ ...capabilities, fetch: checked as boolean })
                      }
                    />
                    <Label htmlFor="cap_fetch" className="cursor-pointer font-normal">
                      HTTP Fetch
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="cap_data"
                      checked={capabilities.data}
                      onCheckedChange={(checked) =>
                        setCapabilities({ ...capabilities, data: checked as boolean })
                      }
                    />
                    <Label htmlFor="cap_data" className="cursor-pointer font-normal">
                      Data Files
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="cap_storage"
                      checked={capabilities.storage}
                      onCheckedChange={(checked) =>
                        setCapabilities({ ...capabilities, storage: checked as boolean })
                      }
                    />
                    <Label htmlFor="cap_storage" className="cursor-pointer font-normal">
                      Key-Value Storage
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="cap_trajectory"
                      checked={capabilities.trajectory}
                      onCheckedChange={(checked) =>
                        setCapabilities({ ...capabilities, trajectory: checked as boolean })
                      }
                    />
                    <Label htmlFor="cap_trajectory" className="cursor-pointer font-normal">
                      Trajectory Read
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="cap_mcp"
                      checked={capabilities.mcp}
                      onCheckedChange={(checked) =>
                        setCapabilities({ ...capabilities, mcp: checked as boolean })
                      }
                    />
                    <Label htmlFor="cap_mcp" className="cursor-pointer font-normal">
                      MCP Tools
                    </Label>
                  </div>
                  {capabilities.mcp && (
                    <div className="ml-6 space-y-1">
                      <Label htmlFor="mcp_servers" className="text-xs">
                        Allowed Servers (comma-separated)
                      </Label>
                      <Input
                        id="mcp_servers"
                        value={mcpServers}
                        onChange={(e) => setMcpServers(e.target.value)}
                        placeholder="massive, polygon, exa"
                        className="text-sm"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="cap_evals"
                      checked={capabilities.evals}
                      onCheckedChange={(checked) =>
                        setCapabilities({ ...capabilities, evals: checked as boolean })
                      }
                    />
                    <Label htmlFor="cap_evals" className="cursor-pointer font-normal">
                      Evals Access
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="cap_content"
                      checked={capabilities.content}
                      onCheckedChange={(checked) =>
                        setCapabilities({ ...capabilities, content: checked as boolean })
                      }
                    />
                    <Label htmlFor="cap_content" className="cursor-pointer font-normal">
                      Content Store
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="cap_secrets"
                      checked={capabilities.secrets}
                      onCheckedChange={(checked) =>
                        setCapabilities({ ...capabilities, secrets: checked as boolean })
                      }
                    />
                    <Label htmlFor="cap_secrets" className="cursor-pointer font-normal">
                      Secrets Access
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="cap_agents"
                      checked={capabilities.agents}
                      onCheckedChange={(checked) =>
                        setCapabilities({ ...capabilities, agents: checked as boolean })
                      }
                    />
                    <Label htmlFor="cap_agents" className="cursor-pointer font-normal">
                      Run Agents
                    </Label>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="timeout">Timeout (ms)</Label>
                <Input
                  id="timeout"
                  type="number"
                  value={timeoutMs}
                  onChange={(e) => setTimeoutMs(parseInt(e.target.value) || 5000)}
                  min={1000}
                  max={30000}
                />
              </div>

              {isTool && (
                <Collapsible>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="w-full justify-between">
                      Tool Schema (JSON)
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <Textarea
                      value={toolSchema}
                      onChange={(e) => setToolSchema(e.target.value)}
                      className="font-mono text-xs mt-2"
                      rows={8}
                      placeholder='{"type": "object", "properties": {}}'
                    />
                  </CollapsibleContent>
                </Collapsible>
              )}

              {isEval && (
                <Collapsible defaultOpen>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="w-full justify-between">
                      Config Schema
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <p className="text-xs text-muted-foreground mb-2">
                      Define configuration parameters for this eval function
                    </p>
                    <ConfigSchemaEditor
                      value={configSchema}
                      onChange={setConfigSchema}
                      disabled={isBuiltin}
                      placeholder="Click 'Add' to define parameters"
                    />
                  </CollapsibleContent>
                </Collapsible>
              )}
            </>
          )}
        </div>
      </div>

      {/* Test Runner (bottom) */}
      {testOpen && (
        <div className="border-t bg-muted/30">
          <Collapsible open={testOpen} onOpenChange={setTestOpen}>
            <CollapsibleTrigger asChild>
              <div className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-muted/50">
                <span className="font-medium">Test Runner</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${testOpen ? 'rotate-180' : ''}`} />
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="p-4 pt-0 space-y-4">
                {isEval ? (
                  /* Eval function test inputs */
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-1">
                        <Label>Config Parameters (JSON)</Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs">
                              <p className="text-xs">Function-specific settings passed to <code className="bg-muted px-1 rounded">params</code> argument. Check your function's docstring for expected fields.</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <Textarea
                        value={testParams}
                        onChange={(e) => setTestParams(e.target.value)}
                        className="font-mono text-sm"
                        rows={4}
                        placeholder='{"ticker": "SPY"}'
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-1">
                        <Label>Eval Context (JSON)</Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-sm text-xs">
                              <p className="font-medium mb-1">Simulates an agent run:</p>
                              <ul className="list-disc pl-4 space-y-0.5">
                                <li><code className="bg-muted px-1 rounded">input_data</code> — what was sent to the agent</li>
                                <li><code className="bg-muted px-1 rounded">output_data</code> — agent's response (usually has <code className="bg-muted px-1 rounded">final_output</code>)</li>
                                <li><code className="bg-muted px-1 rounded">date_override</code> — optional date for backtesting</li>
                              </ul>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <Textarea
                        value={testEvalContext}
                        onChange={(e) => setTestEvalContext(e.target.value)}
                        className="font-mono text-sm"
                        rows={4}
                        placeholder={`{
  "input_data": {},
  "output_data": {"final_output": "..."},
  "date_override": "2024-01-15"
}`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Output</Label>
                      <div className="border rounded-md p-3 bg-background min-h-[100px] font-mono text-sm">
                        {testing ? (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Running...
                          </div>
                        ) : testResult ? (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              {testResult.success ? (
                                <Badge variant="outline" className="text-green-600 border-green-600">
                                  <Check className="h-3 w-3 mr-1" />
                                  Success
                                </Badge>
                              ) : (
                                <Badge variant="destructive">
                                  <X className="h-3 w-3 mr-1" />
                                  Error
                                </Badge>
                              )}
                              <span className="text-xs text-muted-foreground">
                                {testResult.execution_time_ms.toFixed(0)}ms
                              </span>
                            </div>
                            <pre className="text-xs overflow-auto max-h-[200px]">
                              {testResult.success
                                ? JSON.stringify(testResult.result, null, 2)
                                : testResult.error}
                            </pre>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">
                            Click "Run Test" to execute the function
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Tool function test inputs */
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Input Parameters (JSON)</Label>
                      <Textarea
                        value={testParams}
                        onChange={(e) => setTestParams(e.target.value)}
                        className="font-mono text-sm"
                        rows={4}
                        placeholder='{"ticker": "AAPL"}'
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Output</Label>
                      <div className="border rounded-md p-3 bg-background min-h-[100px] font-mono text-sm">
                        {testing ? (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Running...
                          </div>
                        ) : testResult ? (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              {testResult.success ? (
                                <Badge variant="outline" className="text-green-600 border-green-600">
                                  <Check className="h-3 w-3 mr-1" />
                                  Success
                                </Badge>
                              ) : (
                                <Badge variant="destructive">
                                  <X className="h-3 w-3 mr-1" />
                                  Error
                                </Badge>
                              )}
                              <span className="text-xs text-muted-foreground">
                                {testResult.execution_time_ms.toFixed(0)}ms
                              </span>
                            </div>
                            <pre className="text-xs overflow-auto max-h-[200px]">
                              {testResult.success
                                ? JSON.stringify(testResult.result, null, 2)
                                : testResult.error}
                            </pre>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">
                            Click "Run Test" to execute the function
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <Button onClick={handleTest} disabled={testing || isNew}>
                  {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                  Run Test
                </Button>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      )}
    </div>
  );
}

export default FunctionEditorPage;
